/* eslint-disable no-magic-numbers */
/* eslint-disable class-methods-use-this */

/* Core stuff */
import Timer from './core/Timer';
import Metronome from './core/Metronome';

import GraphicObjectRepository from './gl/GraphicObjectRepository';

/* Video */
import NoiseScope from './video/NoiseScope';
import PolyFrame from './video/PolyFrame';
import Headshot from './video/Headshot';
import Pusher from './video/Pusher';

/* Audio */
import Feedback from './audio/Feedback';
import Kick from './audio/Kick';

/* Timelines */
import audio_phases from './audio_phases';
import video_phases from './video_phases';

const { min, max, cos, floor, PI } = Math;

const limit = (x, m0, m1) => max(m0, min(m1, x));

const tempo = 110;

const show_osd = true;

const PIH = PI / 2;

let running = false;

let onstart = null;
let onstop = null;

window.addEventListener('error', handleError);

window.addEventListener('DOMContentLoaded', () => {
	stop();
	window.status = 'Click in window to start playing';
	document.addEventListener('click', initialise);
});

let initialised = false;

function initialise() {
	if (initialised) {
		return;
	}
	initialised = true;
	/* eslint-disable no-alert */
	new Animation().then(() => start(), err => alert(err));
}

function handleError({ message, filename, lineno, colno, error }) {
	console.error(`${filename}@${lineno},${colno}: ${message}`);
	console.error(error);
	stop();
}

function stop() {
	running = false;
	document.body.style.background = 'maroon';
	if (onstop) {
		onstop();
	}
}

function start() {
	document.body.style.background = null;
	running = true;
	if (onstart) {
		onstart();
	}
}

function createCanvas() {
	document.body.innerHTML = '';
	const el = document.createElement('canvas');
	el.classList.add('viewport');
	document.body.appendChild(el);
	return el;
}

const debugMode = /^#?./.test(window.location.hash);

function createOSD() {
	if (debugMode) {
		const el = document.createElement('div');
		el.classList.add('osd');
		document.body.appendChild(el);
		return data => {
			el.innerHTML = '';
			for (const key of Object.keys(data)) {
				const row = document.createElement('div');
				row.innerHTML = `${key}: ${data[key]}`;
				el.appendChild(row);
			}
		};
	} else {
		return data => {};
	}
}

function getVideo(canvas) {
	try {
		const ret = canvas.getContext('webgl');
		if (ret) {
			return ret;
		}
	} catch (e) {
		void 0;
	}
	throw new Error('Your browser does not support WebGL');
}

function getAudio() {
	try {
		return new window.AudioContext();
	} catch (e) {
		void 0;
	}
	throw new Error('Your browser does not support the Web Audio API');
}

function decorate_layer(phase, offset) {
	const length = phase.getBeatLength();
	const prelude = phase.getPreludeLength();
	const postlude = phase.getPostludeLength();
	phase.length = length;
	phase.offset = offset;
	phase.start = prelude === null ? 0 : phase.offset - prelude;
	phase.end = postlude === null ? Infinity : offset + length + postlude;
}

function init_phase_seq(name, seq) {
	console.log(`${name} phases`);
	const flat = [];
	const length = seq.reduce((offset, phase) => {
		const length = phase.reduce((max, layer) => {
			decorate_layer(layer, offset);
			flat.push(layer);
			const length = layer.getBeatLength();
			if (length > 0) {
				console.log([offset, length, layer.constructor.name].join(' \t'));
			} else {
				console.log([offset, '-', layer.constructor.name].join(' \t'));
			}
			return Math.max(max, length);
		}, 0);
		return offset + length;
	}, 0);
	return { layers: flat, length: length };
}

function generate_configuration(layers, time, conf) {
	const active = [];
	for (const layer of layers) {
		if (time.absolute >= layer.start && time.relative <= layer.end) {
			const rel = time.relativeTo(layer.offset);
			const passage = layer.length > 0 ? rel.relative / layer.length : 0;
			const passageClamped = limit(passage, 0, 1);
			layer.run({
				time: rel,
				passage,
				passageClamped,
				...conf
			});
			active.push(layer);
		}
	}
	return active;
}

async function Animation() {

	const canvas = createCanvas();
	const gl = getVideo(canvas);
	const audio = getAudio();

	/* Graphics providers */
	const gor = new GraphicObjectRepository(gl);
	const noise = new NoiseScope(gl, gor);
	const frame = new PolyFrame(gl, gor, 50);
	const headshot = new Headshot(gl, gor);
	const pusher = new Pusher(gl, gor);

	await headshot.load();
	await pusher.load();

	/* Audio providers */
	const feedback = new Feedback(audio);
	const kick = new Kick(audio);

	/* Decorate phase sequences and calculate total beats */
	const { layers: video_layers, length: total_video_beats } = init_phase_seq('Video', video_phases);
	const { layers: audio_layers, length: total_audio_beats } = init_phase_seq('Audio', audio_phases);

	console.log(`Total beats: ${total_video_beats} / ${total_audio_beats}`);

	/* Measures relative time */
	const timer = new Timer();

	/* Provides richer timing data from a Timer instance */
	const metronome = new Metronome({
		timer,
		tempo,
		beats_per_bar: 4,
		total_beats: total_video_beats
	});

	{
		const hash = window.location.hash.replace(/^#/, '');
		if (String(parseInt(hash, 10)) === hash) {
			metronome.offsetBy(parseInt(hash, 10));
		}
	}

	/* Diagnostic info */
	const osd = createOSD();

	/* Mousewheel handler for diangostics */
	document.addEventListener('wheel', event => {
		const amount = event.deltaY > 0 ? -1 : event.deltaY < 0 ? 1 : 0;
		metronome.offsetBy(amount);
		event.preventDefault();
	});

	/* Used for deciding whether to update OSD */
	let prev_pos = -1;

	/* Most recent request for renderAnimationFrame */
	let rafReq = null;

	/* Bind start/stop commands */
	const _onstart = () => {
		onstart = null;
		timer.deferReset();
		rafReq = requestAnimationFrame(renderFrame);
	};
	onstart = _onstart;
	onstop = () => {
		cancelAnimationFrame(rafReq);
		rafReq = null;
		renderVideo({ layers: {} });
		renderAudio({ layers: {} });
		onstart = _onstart;
	};

	return;

	function renderVideo({ layers, bg_opts, noise_opts, frame_opts, headshot_opts, pusher_opts }) {
		/* Background */
		const bg = 0.07 + 0.26 * (layers.flash ? bg_opts.flash : 0);
		gl.clearColor(bg, bg, bg, 1.0);
		gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);

		/* Noise */
		if (layers.noise) {
			gl.disable(gl.BLEND);
			noise.draw(noise_opts);
		}

		/* Frame */
		if (layers.frame) {
			gl.disable(gl.BLEND);
			frame.draw(frame_opts);
		}

		/* Pusher */
		if (layers.pusher) {
			gl.enable(gl.BLEND);
			gl.blendEquation(gl.FUNC_ADD);
			gl.blendFunc(gl.ONE_MINUS_DST_ALPHA, gl.ONE);
			pusher.draw(pusher_opts);
		}

		/* Headshot */
		if (layers.headshot) {
			gl.enable(gl.BLEND);
			gl.blendEquation(gl.FUNC_ADD);
			gl.blendFunc(gl.ONE_MINUS_DST_ALPHA, gl.ONE);
			headshot.draw(headshot_opts);
		}

		/* Draw content */
		gl.enable(gl.BLEND);
		gl.blendEquation(gl.FUNC_ADD);
		gl.blendFunc(gl.ONE_MINUS_DST_ALPHA, gl.DST_ALPHA);
		//		const testOpts = frame.defaultOpts();
		//		testOpts.aspect_correction = scale;
		//		testOpts.star = pulse * 10;
		//		testOpts.star_style = 1;
		//		testOpts.thickness = 0.1;
		//		testOpts.size = 15;
		//		testOpts.sides = 60;
		//		testOpts.rotation = -frame_opts.rotation * 2;
		//		testOpts.base_color = new Float32Array([1, 0, 1, 1]);
		//		frame.draw(testOpts);
	}

	function renderAudio({ layers, feedback_opts, kick_opts }) {
		if (layers.feedback) {
			feedback.play(feedback_opts);
		} else {
			feedback.stop();
		}
		if (layers.kick) {
			kick.play(kick_opts);
		}
	}

	function renderFrame() {
		if (!running) {
			return;
		}

		const size_data = configureViewport();

		const aspect = size_data[2] / size_data[3];
		const scale = [0.01 / max(1, aspect), 0.01 * min(1, aspect)];

		const period = 60 / tempo;

		/* Video options */
		const video_conf = {
			layers: {
				flash: false,
				noise: false,
				frame: false,
				pusher: false,
				headshot: false,
			},
			bg_opts: { flash: 0 },
			noise_opts: noise.defaultOpts(),
			frame_opts: frame.defaultOpts(),
			pusher_opts: pusher.defaultOpts(),
			headshot_opts: headshot.defaultOpts(),
		};

		/* Audio options */
		const audio_conf = {
			layers: {
				feedback: false,
				kick: false
			},
			feedback_opts: feedback.defaultOpts(),
			kick_opts: kick.defaultOpts()
		};

		/* get timing info */
		timer.tick();
		const time = metronome.get();

		/* Pre-calculate "pulse" */
		const pulse = cos(PIH * time.offBeat);

		const all_conf = { aspect, scale, period, pulse };

		/* Video configuration for this frame */
		const vp = generate_configuration(video_layers, time, { ...all_conf, ...video_conf });

		/* Audio */
		const ap = generate_configuration(audio_layers, time, { ...all_conf, ...audio_conf });

		renderVideo(video_conf);

		renderAudio(audio_conf);

		/* Update OSD */
		if (show_osd && floor(prev_pos * 3) !== floor(time.absolute * 3)) {
			osd({
				rate: `${(1 / time.delta).toFixed(0)} Hz`,
				beat: `${time.beat} / ${total_video_beats}`,
				'video phase': vp.map(p => p.constructor.name).join(', '),
				'audio phase': ap.map(p => p.constructor.name).join(', '),
			});
		}
		prev_pos = time.absolute;

		//rafReq = setTimeout(renderFrame, 0);
		rafReq = requestAnimationFrame(renderFrame);
	}

	function configureViewport() {
		const w = max(1, canvas.offsetWidth);
		const h = max(1, canvas.offsetHeight);
		const cw = max(1, canvas.clientWidth);
		const ch = max(1, canvas.clientHeight);
		const size_memo = configureViewport.memo = configureViewport.memo || [0, 0, 0, 0];
		const size_data = configureViewport.tmp = configureViewport.tmp || [0, 0, 0, 0];
		size_data.splice(0, 4, w, h, cw, ch);
		let same = true;
		for (let i = 0; i < size_data.length; i++) {
			same = same && size_memo[i] === size_data[i];
		}
		if (!same) {
			size_memo.splice(0, 4, ...size_data);
			/* Set canvas size */
			canvas.width = cw;
			canvas.height = ch;
			/* Map clip space to entire buffer */
			gl.viewport(0, 0, gl.drawingBufferWidth, gl.drawingBufferHeight);
		}
		return size_data;
	}
}
